import * as ngCore from '@angular/core'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'

import * as me from '../../me'
import * as apps from '../apps'
import * as contexts from '../contexts'
import * as scenarios from '../scenarios'
import * as tenants from '../tenants'
import * as userAssignment from '../user-assignment'
import * as zones from '../zones'

import { Api } from './api'
import * as effects from './effects'
import * as models from './models'
import type { TypeParams } from './types'

/**
 * Provides a collection of users within the Admin portal
 */
@ngCore.Injectable({ providedIn: 'root' })
export class Collection extends ec.Collection<TypeParams> {
  constructor() {
    const _contexts = ngCore.inject(contexts.Feature)
    const _meFeature = ngCore.inject(me.Feature)

    // Keep parent collections initialized because admin context derives tenant,
    // zone, app, and scenario scopes from their current entity state.
    ngCore.inject(tenants.Collection)
    ngCore.inject(zones.Collection)
    ngCore.inject(apps.Collection)
    ngCore.inject(scenarios.Collection)
    // NOTE: Important to be after injecting collections above
    const _userAssignmentFeature = ngCore.inject(userAssignment.Feature)
    super({
      api: ngCore.inject(Api),
      identify: user => user.id,
      name: ec.Name.Predefined.AdminUser,
      plugins: {
        additionalFactories: [
          new ds.table.ec.Factory<TypeParams>(collection => ({
            rowConfig: ({ entity: user }) => {
              const activeContext =
                _contexts.selectors.activeEntityContext.asSignal()
              const currentUser = _meFeature.selectors.currentUser.asSignal()
              const canManageProfile = models.User.canManageProfile({
                activeContext,
                currentUser,
              })
              const canUnassignUser =
                activeContext !== undefined &&
                userAssignment.models.User.canManageUsersForContext(
                  currentUser,
                  activeContext,
                )

              return {
                iconsConfigs: [
                  ...(canManageProfile
                    ? [
                        {
                          class: 'atn-icon atn-edit atn-blue',
                          onClick: () => {
                            collection.plugins.editingPlugin.feature.actions.edit
                              .from(
                                `User.Table.Row-${user.id}.Icons.Edit.Clicked`,
                              )
                              .dispatch({ id: user.id })
                          },
                        },
                        {
                          class: 'atn-icon atn-trash atn-red',
                          onClick: () => {
                            collection.plugins.editingPlugin.feature.actions.markForDeletion
                              .from(
                                `User.Table.Row-${user.id}.Icons.Delete.Clicked`,
                              )
                              .dispatch({ id: user.id })
                          },
                        },
                      ]
                    : []),
                  ...(canUnassignUser
                    ? [
                        {
                          class: 'atn-icon atn-close atn-red',
                          onClick: () => {
                            _userAssignmentFeature.actions.markUserForUnassignment
                              .from(
                                `User.Table.Row-${user.id}.Icons.Unassign.Clicked`,
                              )
                              .dispatch({ user })
                          },
                        },
                      ]
                    : []),
                ],
              }
            },
          })),
        ],
      },
    })
    this.feature.registerEffects(
      effects.setFilters(this, _contexts),
      effects.refreshUsersOnAccessAssignmentSuccess(
        this,
        _userAssignmentFeature,
        _contexts,
      ),
      effects.refreshUsersOnCreateSuccess(this, _contexts),
      effects.syncAccessFlagsAttributeVisibility(this, _contexts),
    )
  }
}
